/**
 * Block textures for a guest who doesn't have Minecraft on this computer: the official game file (client.jar) of the
 * host's version, downloaded by *this browser* straight from Mojang's own servers — the same file the Minecraft
 * launcher downloads. MC Planner never stores, relays or serves it (Mojang's game files may not be redistributed,
 * see CLAUDE.md); it stays on this computer like a jar the person picked themselves (IndexedDB, controller.loadAssets).
 *
 * The person confirms once that they own Minecraft: Java Edition and accept the Minecraft EULA (like map tools such
 * as BlueMap ask before they download the client) — remembered in this browser, later visits need no click.
 *
 * Mojang's servers answer cross-origin requests (checked 2026-09-26 from the MC Planner origin), so this works on the
 * website and in the desktop app alike.
 */

const MANIFEST = "https://piston-meta.mojang.com/mc/game/version_manifest_v2.json";
const OK_KEY = "mcp-mojang-ok-v1";
export const EULA_URL = "https://www.minecraft.net/eula";

export interface MojangClient {
  /** the version Mojang has (the host's, or the newest release when the host's isn't known to Mojang) */
  id: string;
  exact: boolean;
  url: string;
  sha1: string;
  size: number;
}

/** The person said "I own Minecraft: Java Edition and accept the EULA" in this browser before. */
export function mojangAccepted() {
  try { return localStorage.getItem(OK_KEY) === "1"; } catch { return false; }
}
export function acceptMojang() {
  try { localStorage.setItem(OK_KEY, "1"); } catch { /* private mode: ask again next time */ }
}

const cache = new Map<string, Promise<MojangClient>>();

/** Which client.jar fits a world of `version` ("26.2", "1.21.5", "26.3-snapshot-1" …). A few small JSON requests. */
export function findClient(version: string): Promise<MojangClient> {
  const key = version.trim();
  let p = cache.get(key);
  if (!p) {
    p = (async () => {
      const m = (await getJson(MANIFEST)) as { latest: { release: string }; versions: { id: string; type: string; url: string }[] };
      const want = key.replace(/\s.*$/, ""); // "26.2 (modded)" → "26.2"
      const hit = m.versions.find((v) => v.id === want) ?? m.versions.find((v) => v.id === m.latest.release);
      if (!hit) throw new Error("Mojang's version list is empty.");
      const v = (await getJson(hit.url)) as { downloads?: { client?: { url: string; sha1: string; size: number } } };
      const c = v.downloads?.client;
      if (!c) throw new Error(`Mojang has no game file for ${hit.id}.`);
      return { id: hit.id, exact: hit.id === want, url: c.url, sha1: c.sha1, size: c.size };
    })();
    cache.set(key, p);
    p.catch(() => cache.delete(key));
  }
  return p;
}

async function getJson(url: string) {
  const r = await fetch(url, { cache: "no-cache" });
  if (!r.ok) throw new Error(`Mojang's server answered ${r.status}.`);
  return r.json();
}

/** Downloads (and checks) the game file. `onProgress(received, total)` while it comes in. */
export async function downloadClient(c: MojangClient, onProgress: (got: number, total: number) => void): Promise<Blob> {
  const r = await fetch(c.url);
  if (!r.ok || !r.body) throw new Error(`Mojang's server answered ${r.status}.`);
  const total = Number(r.headers.get("content-length")) || c.size;
  const reader = r.body.getReader();
  const parts: Uint8Array[] = [];
  let got = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value);
    got += value.byteLength;
    onProgress(got, total);
  }
  const blob = new Blob(parts as BlobPart[], { type: "application/java-archive" });
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-1", await blob.arrayBuffer()));
  const hex = Array.from(digest, (b) => b.toString(16).padStart(2, "0")).join("");
  if (hex !== c.sha1) throw new Error("The download from Mojang was damaged. Try again.");
  return blob;
}

export const fmtMB = (n: number) => `${n < 10e6 ? (n / 1e6).toFixed(1) : Math.round(n / 1e6)} MB`;
